import type { PaletteOptions } from '@mui/material/styles';
import primitivesApp from 'themes/primitives-app';
import primitives from 'themes/primitives';

const { gray, blue, green, yellow, red, orange, pink, purple, teal, overlay, jadeite, blueberry, gold } = primitivesApp;
const { white, black } = primitives.base;
// White-alpha overlays for lightening surfaces in dark mode
const whiteAlpha10 = primitives.overlay['white-10']; // #ffffff1a (~10% white)

const paletteDark: PaletteOptions = {
  // ---- MUI Standard Palette ----
  // Shift lighter so brand colors remain readable on dark surfaces
  primary: {
    light: blue[650],
    main: blue[700],
    dark: blue[930],     // #1f74ba
    contrastText: white,
  },
  secondary: {
    light: blue[500],    // #9bcbeb
    main: blue[650],
    dark: blue[700],
    contrastText: white,
  },
  success: {
    light: green[400],   // #67de6f — brighter
    main: green[500],    // #5ac402 — more vibrant on dark
    dark: green[750],    // #118819
    contrastText: white,
  },
  info: {
    light: blue[400],    // #bfd9fc
    main: blue[650],     // #74b8ff — lighter, more visible on dark
    dark: blue[800],     // #3a72b5
    contrastText: white,
  },
  warning: {
    light: yellow[400],  // #f5a623
    main: yellow[500],   // #f29d50 — amber, stays visible on dark
    dark: yellow[650],   // #eaa111
    contrastText: white,
  },
  error: {
    light: red[250],     // #ff6e6e — stays vivid on dark
    main: red[400],      // #da6561 — slightly lighter
    dark: red[700],      // #d70015
    contrastText: white,
  },

  // ---- Text ----
  // Inverted from light mode — light text on dark backgrounds
  text: {
    primary: gray[50],    // #f5f5f5 — near white, primary readable text
    secondary: gray[550],  // #dfdfdf — secondary, medium light
    tertiary: gray[550],   // #c9c7c5 — muted tertiary
    disabled: gray[725],   // #a1afb5 — clearly disabled
    contrastText: black,   // For text on light-colored surfaces
    strong: white,         // #ffffff — strongest emphasis
    input: gray[400],      // #ebeff2 — input field text
    warning: yellow[300],
  },

  // ---- Icons ----
  icon: {
    light: gray[550],    // #c9c7c5 — subtle
    main: gray[300],     // #f4f4f4 — primary icon
    dark: white,         // #ffffff — strongest
    brand: blue[100],
    muted: gray[725],    // #a1afb5
    secondary: gray[725],
    primary: gray[50],
  },

  // ---- MUI Action States ----
  action: {
    active: gray[250],         // #f5f5f5
    hover: gray[1250],
    hoverLight: overlay.white[20],
    hoverOpacity: 0.08,
    selected: blue[800],       // #3a72b5
    selectedDark: overlay.black[20],
    selectedOpacity: 0.08,
    disabled: gray[725],       // #a1afb5
    disabledBackground: gray[1200], // #3e3e3c
    disabledOpacity: 0.45,
    focus: blue[700],          // #5cb3ea
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },

  // ---- Backgrounds ----
  // Darkest = default canvas; progressively lighter = more elevated
  background: {
    default: gray[1350],
    paper: gray[1300],
    layer1: gray[1025],           // #706e6b — layered container
    subtle: gray[1200],           // #3e3e3c — subtle hover/row targets
    elevated: gray[1250],
    input: gray[1250],
    hint: yellow[700],            // #c67d00 — dark amber warning highlight
    hintSubtle: yellow[900], 
    info: blue[990],              // #1a4985 — dark blue info surface
    selected: blue[800],          // #3a72b5 — visible selection bg
    payeeHighlight: green[950],   // #116611 — dark green for payee rules
    settingsBarBackground: blue[990], // #1a4985
    dark: gray[1300],
    authDialog: gray[1400],          // #22262a — matches auth iframe dark background
    sunken: gray[1300],
  },

  // ---- Backdrop / Overlays ----
  backdrop: {
    fieldHover: whiteAlpha10,         // lighten field on hover (dark bg)
    iconDimmed: overlay.blackAlpha[50], // dims icons (still valid on dark)
    registerSplits: overlay.blueAlpha[3], // faint blue tint
    navHover: overlay.white[20],
    navSelected: overlay.white[20],
  },

  // ---- Borders ----
  // Lighter values for visibility against dark backgrounds
  border: {
    default: gray[850],
    // light: gray[1200],     // #3e3e3c — very subtle border
    light: gray[1025],     // #706e6b — subtle but visible against paper (#3e3e3c)
    main: gray[850],       // #969492 — standard visible border
    strong: gray[1200],
    dark: gray[725],       // #a1afb5 — strong border (inverted: lighter in dark)
    focus: blueberry[100], // #496ed8 — focus ring stays similar
    calendarFocus: blueberry[70], // #89a0e6
  },

  divider: whiteAlpha10, // white overlay divider for dark surfaces

  // ---- Tooltip ----
  tooltip: {
    light: gray[200],         // #f6f7f8 — light tooltip background
    dark: gray[1250],         // #333333 — dark tooltip background
    text: gray[250],          // #f5f5f5 — readable text on paper-bg tooltip
    contrastText: gray[250],  // #f5f5f5 — text on dark tooltip background
  },

  // ---- Amount / Numbers ----
  // Brighter/lighter for readability on dark backgrounds
  amount: {
    positive: green[400],  // #67de6f — brighter green
    negative: red[250],    // #ff6e6e — lighter red
  },

  // ---- Severity Status ----
  status: {
    lowLight: green[400],    // #67de6f — brighter
    mediumMain: yellow[400], // #f5a623 — amber, visible on dark
    highMain: red[250],      // #ff6e6e — lighter red
    attentionLight: gold[30],  // #f9efd4 — stays
    attentionMain: gold[100],  // #eac563 — stays
    dangerMain: red[400],    // #da6561 — slightly lighter
    incomeLight: green[900],   // #008e38 — dark green for calendar cells
    expenseLight: yellow[700], // #c67d00 — dark amber for calendar cells
    overdueLight: red[900],    // #330000 — dark red for calendar cells
  },

  // ---- Data Visualization ----
  // Brighter/more vibrant palette for charts on dark backgrounds
  data: {
    sequence1: [
      blue[650], yellow[750], green[400], orange[500],
      purple[100], pink[200], teal[100], purple[300],
      teal[400], orange[600],
    ],
  },

  // ---- Legend ----
  legend: {
    income: green[400],  // #67de6f — brighter for dark bg
    bill: blue[500],     // #9bcbeb — lighter for dark bg
  },

  // ---- Links ----
  link: {
    main: blue[500],       // #9bcbeb — light, readable on dark
    dark: blue[650],       // #74b8ff
    interactive: blue[650], // #74b8ff
  },

  // ---- App-Specific ----
  other: {
    overdueTxn: yellow[400],  // #f5a623 — more visible on dark
    dueTxn: blue[500],        // #9bcbeb — lighter
    scheduledChip: yellow[300], // #f5dbb5 — stays
  },

  reports: {
    incomeColor: jadeite[100],  // #62b59e — stays (unique brand color)
    positiveColor: green[400],  // #67de6f — brighter on dark
    negativeColor: red[250],    // #ff6e6e — lighter on dark
    positiveDimmed: green[200], // #add7ad — more visible than light mode's 100
    negativeDimmed: red[200],   // #ef7d5b — more vibrant on dark
  },

  checkbox: {
    background: gray[1300],
    border: gray[850],
    color: gray[0],
  },
};

export default paletteDark;
